// Lightweight API client for backend requests
// Provides: base URL management, default headers, JSON parsing, and error handling
import type { InternalCompanySchema } from "./companies";
import { getCurrentRequestId } from "./request-logger";
import type { OperatingModeRequest, PauseReasonRequest, PlatformControl } from "./rollout";
import { captureApiClientError, inferFeatureArea } from "./sentry";

/**
 * ============================================================================
 * HOW TO ADD NEW API ROUTES AND TYPES
 * ============================================================================
 *
 * 1. DEFINE TYPES (Request/Response schemas)
 *    - Add your request/response types above the BackendApi class
 *    - Use descriptive names: `MyFeatureRequest`, `MyFeatureResponse`
 *    - Include JSDoc comments for complex fields
 *    - Example:
 *
 *      export type MyFeatureRequest = {
 *        company_id: string;
 *        workspace_id: string;
 *        date_from: string; // ISO date string
 *      };
 *
 *      export type MyFeatureResponse = {
 *        items: Array<{
 *          id: string;
 *          name: string;
 *          count: number;
 *        }>;
 *      };
 *
 * 2. ADD METHODS TO BackendApi CLASS
 *    - Add methods to the BackendApi class below
 *    - Use descriptive method names: `getMyFeature`, `createMyFeature`, `updateMyFeature`
 *    - Follow the patterns shown in the example methods (GET, POST, PUT)
 *
 *    GET Example (with query params):
 *      async getMyFeature(params: { company_id: string; limit?: number }): Promise<MyFeatureResponse> {
 *        return this.client.request<MyFeatureResponse>('/api/v1/my-feature/', {
 *          method: 'GET',
 *          searchParams: {
 *            company_id: params.company_id,
 *            limit: params.limit ?? 100,
 *          },
 *        });
 *      }
 *
 *    POST Example (with request body):
 *      async createMyFeature(payload: MyFeatureRequest): Promise<MyFeatureResponse> {
 *        return this.client.request<MyFeatureResponse>('/api/v1/my-feature/', {
 *          method: 'POST',
 *          body: JSON.stringify(payload),
 *        });
 *      }
 *
 *    PUT Example (with path params and body):
 *      async updateMyFeature(id: string, payload: { name: string }): Promise<MyFeatureResponse> {
 *        return this.client.request<MyFeatureResponse>(`/api/v1/my-feature/${id}`, {
 *          method: 'PUT',
 *          body: JSON.stringify(payload),
 *        });
 *      }
 *
 * 3. USAGE IN FEATURES
 *    - Import the BackendApi instance and types in your React Query hooks
 *    - Example:
 *
 *      import { BackendApi, MyFeatureRequest, MyFeatureResponse } from '@/lib/api-client';
 *
 *      const api = new BackendApi();
 *      const data = await api.getMyFeature({ company_id: '123' });
 */

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ApiClientOptions = {
  baseUrl?: string;
  getAuthToken?: () => Promise<string | undefined> | string | undefined;
  defaultHeaders?: Record<string, string>;
};

export class ApiError extends Error {
  status: number;
  data?: unknown;
  endpoint?: string;
  method?: string;
  backendRequestId?: string;

  constructor(
    message: string,
    status: number,
    data?: unknown,
    opts?: { endpoint?: string; method?: string; backendRequestId?: string },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.endpoint = opts?.endpoint;
    this.method = opts?.method;
    this.backendRequestId = opts?.backendRequestId;
  }
}

export class ApiClient {
  private baseUrl: string;
  private getAuthToken?: ApiClientOptions["getAuthToken"];
  private defaultHeaders: Record<string, string>;

  constructor(options?: ApiClientOptions) {
    // Client-side: Always use relative URLs (empty string) to route through Next.js API routes
    // Server-side: Use provided baseUrl or API_BASE_URL env var
    // NOTE: We intentionally ignore NEXT_PUBLIC_API_BASE_URL for client-side calls
    // to ensure all requests go through Next.js API routes for proper auth handling
    this.baseUrl = options?.baseUrl ?? "";
    this.getAuthToken = options?.getAuthToken;
    this.defaultHeaders = {
      "Content-Type": "application/json",
      ...options?.defaultHeaders,
    };
  }

  private async buildHeaders(extra?: Record<string, string>): Promise<HeadersInit> {
    const headers: Record<string, string> = { ...this.defaultHeaders, ...(extra ?? {}) };
    const token = await (typeof this.getAuthToken === "function" ? this.getAuthToken() : undefined);
    if (token) headers["Authorization"] = `Bearer ${token}`;

    // Forward X-Request-Id header if available (from AsyncLocalStorage)
    const requestId = getCurrentRequestId();
    if (requestId) {
      headers["X-Request-Id"] = requestId;
    }

    return headers;
  }

  async request<TResponse>(
    path: string,
    init?: RequestInit & { searchParams?: Record<string, string | number | boolean | undefined> },
  ): Promise<TResponse> {
    // If baseUrl is not set or is empty, use relative URLs (for Next.js API routes)
    let url: string;
    if (!this.baseUrl || this.baseUrl === "") {
      // Relative URL - build query string manually
      const searchParams = new URLSearchParams();
      if (init?.searchParams) {
        Object.entries(init.searchParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.set(key, String(value));
          }
        });
      }
      const queryString = searchParams.toString();
      url = queryString ? `${path}?${queryString}` : path;
    } else {
      // Absolute URL - use URL constructor
      const urlObj = new URL(path, this.baseUrl);
      if (init?.searchParams) {
        Object.entries(init.searchParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) urlObj.searchParams.set(key, String(value));
        });
      }
      url = urlObj.toString();
    }

    const response = await fetch(url, {
      ...init,
      headers: await this.buildHeaders(init?.headers as Record<string, string> | undefined),
    });

    const contentType = response.headers.get("content-type") ?? "";
    const isJson = contentType.includes("application/json");
    const data = isJson
      ? await response.json().catch(() => undefined)
      : await response.text().catch(() => undefined);

    if (!response.ok) {
      const message =
        isJson && data && typeof data === "object" && "message" in data
          ? String((data as { message: unknown }).message)
          : response.statusText || "Request failed";
      const method = (init?.method ?? "GET").toUpperCase();
      const backendRequestId = response.headers.get("x-request-id") ?? undefined;
      const error = new ApiError(message, response.status, data, {
        endpoint: path,
        method,
        backendRequestId,
      });
      captureApiClientError(error, {
        requestId: getCurrentRequestId(),
        backendRequestId,
        endpoint: path,
        method,
        status: response.status,
        featureArea: inferFeatureArea(path),
      });
      throw error;
    }

    return data as TResponse;
  }
}

// ============================================================================
// DASHBOARD TYPES
// ============================================================================

export type DashboardViewResponse = {
  kpis: {
    totalPortfolio: {
      count: number;
      totalOutstanding: string;
    };
    contactRate: {
      percentage: number;
      contacted: number;
      total: number;
    };
    moneyCollected: {
      amount: string;
      paidInFullCount: number;
    };
    recoveryRate: {
      percentage: number;
      conversionRate: number;
    };
    inRepaymentPlans: {
      amount: string;
      activePlansCount: number;
    };
  };
  pipelineFunnel: Array<{
    stage: string;
    count: number;
    percentage: number;
  }>;
  weeklyActivity: Array<{
    week: string;
    callsMade: number;
    emailsSent: number;
    moneyCollected: string;
  }>;
  statusDistribution: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
};

// ============================================================================
// DEBTORS TYPES
// ============================================================================

export type PaymentLikelihoodScore = {
  score: number;
  staticScore: number;
  behaviouralAdjustment: number;
  breakdown: Record<string, unknown>;
};

export type CaseEmail = {
  id: string;
  company_id: string;
  case_id: string;
  email_address: string;
  created_at: string;
  updated_at: string;
};

export type DebtorsListViewResponse = {
  cases: Array<{
    id: string;
    debtorName: string;
    accountReference: string;
    externalCaseReference: string;
    currentBalance: string;
    originalPrincipalAmount: string;
    currency: string;
    ageOfDebtMonths: number;
    status: string;
    stage: string;
    debtorEmail: string | null;
    emails: CaseEmail[] | null;
    debtorPhone: string | null;
    lastContacted: {
      date: string;
      method: string;
      eventType: string;
    } | null;
    caseOwner: {
      id: string;
      firstName: string;
      lastName: string;
    } | null;
    createdAt: string;
    paymentLikelihoodScore: PaymentLikelihoodScore;
  }>;
  totalCount: number;
  filters: {
    availableStatuses: string[];
    availableStages: string[];
  };
};

export type DebtorDetailViewResponse = {
  case: {
    id: string;
    debtorName: string;
    clientName: string | null;
    accountReference: string;
    externalCaseReference: string;
    debtorEmail: string | null;
    emails: CaseEmail[] | null;
    debtorPhone: string | null;
    currency: string;
    originalPrincipalAmount: string;
    currentBalance: string;
    dueDate: string | null;
    stage: string;
    status: string;
    workflowInstanceId: string | null;
    paymentPlanId: string | null;
    caseOwner: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    } | null;
    notes: string | null;
    meta: Record<string, unknown> | null;
    createdAt: string;
    updatedAt: string;
    paymentLikelihoodScore: PaymentLikelihoodScore;
  };
  interactionHistory: Array<{
    id: string;
    eventType: string;
    createdAt: string;
    createdBy: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    data: Record<string, unknown>;
  }>;
  availableActions: string[];
};

// ============================================================================
// RECOMMENDED ACTIONS TYPES
// ============================================================================

export type StartAutomatedWorkflowActionParameters = {
  case_id: string;
};

export type RecommendedActionsViewResponse = {
  summary: {
    totalPending: number;
    totalInProgress: number;
    totalCompleted: number;
    byType: Record<string, number>;
  };
  actions: Array<{
    id: string;
    recommendedActionType: string;
    status: string;
    title: string;
    description: string | null;
    caseId: string | null;
    case: {
      debtorName: string;
      accountReference: string;
      currentBalance: string;
    } | null;
    data: Record<string, unknown>;
    createdAt: string;
    createdBy: {
      id: string;
      firstName: string;
      lastName: string;
    };
  }>;
  totalCount: number;
};

// ============================================================================
// DATA UPLOAD TYPES
// ============================================================================

export type DataUploadViewResponse = {
  currentData: {
    totalCases: number;
    totalOutstandingDebt: string;
    currency: string;
  };
  requiredFields: string[];
  uploadEndpoint: string;
};

// ============================================================================
// COMPLIANCE & AUDIT TYPES
// ============================================================================

export type ComplianceAuditViewResponse = {
  summary: {
    totalContacts: number;
    compliant: number;
    callsMade: number;
    emailsSent: number;
    smsSent: number;
  };
  events: Array<{
    id: string;
    timestamp: string;
    case: {
      id: string;
      debtorName: string;
      accountReference: string;
    };
    agent: {
      id: string;
      firstName: string;
      lastName: string;
      identifier: string;
    };
    channel: string;
    eventType: string;
    outcome: string;
    notes: string | null;
    complianceStatus: string;
    data: Record<string, unknown>;
  }>;
  totalCount: number;
  filters: {
    availableChannels: string[];
    availableOutcomes: string[];
    availableAgents: Array<{
      id: string;
      name: string;
    }>;
  };
};

// ============================================================================
// SETTINGS TYPES
// ============================================================================

export type SettingsViewResponse = {
  company: {
    id: string;
    name: string;
  };
  teamMembers: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    companyRole: string;
    createdAt: string;
  }>;
  totalCount: number;
};

// ============================================================================
// USER TYPES
// ============================================================================

export type UserResponse = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  company_id: string;
  company_role: string;
  clerk_user_id: string | null;
  created_at: string;
  updated_at: string;
};

// ============================================================================
// WORKFLOW TYPES
// ============================================================================

export type WorkflowStatus = "DRAFT" | "PUBLISHED";
export type WorkflowMode = "FULLY_AUTONOMOUS" | "HUMAN_REVIEW";
export type WorkflowStepType = "CALL" | "EMAIL" | "SMS";

export type WorkflowStepSchema = {
  id?: string;
  workflowVersionId?: string;
  stepType: WorkflowStepType;
  label: string;
  day: number;
  time?: string;
  stepInput: Record<string, unknown>;
  order?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type WorkflowVersionSchema = {
  id: string;
  workflowId: string;
  version: number;
  status: WorkflowStatus;
  mode?: WorkflowMode;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  steps?: WorkflowStepSchema[];
};

export type WorkflowSchema = {
  id: string;
  companyId: string;
  name: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  activeCasesCount?: number;
  latestVersion?: WorkflowVersionSchema;
  hasPublishedVersion?: boolean;
};

export type WorkflowPaginationSchema = {
  items: WorkflowSchema[];
  count: number;
  limit: number;
  offset: number;
};

export type WorkflowVersionPaginationSchema = {
  items: WorkflowVersionSchema[];
  count: number;
  limit: number;
  offset: number;
};

export type WorkflowCreateCommandRequest = {
  name: string;
  is_default?: boolean;
};

export type WorkflowUpdateCommandRequest = {
  workflow_id: string;
  name?: string;
  is_default?: boolean;
};

export type WorkflowVersionCreateCommandRequest = {
  workflow_id: string;
  mode?: WorkflowMode;
};

export type WorkflowVersionUpdateCommandRequest = {
  workflow_version_id: string;
  mode?: WorkflowMode;
  status?: WorkflowStatus;
  steps?: WorkflowStepSchema[];
};

export type WorkflowVersionPublishCommandRequest = {
  workflow_version_id: string;
};

export type WorkflowVersionArchiveCommandRequest = {
  workflow_version_id: string;
};

// ============================================================================
// WORKFLOW INSTANCE TYPES
// ============================================================================

export type WorkflowInstanceType = "BASIC_CASE_WORKFLOW" | "AUTONOMOUS_CASE_WORKFLOW";

export type WorkflowInstanceStatus = "CREATED" | "IN_PROGRESS" | "COMPLETED";

export type WorkflowInstanceSchema = {
  id: string;
  workflowId: string;
  workflowVersionId: string;
  instanceType: WorkflowInstanceType;
  status: WorkflowInstanceStatus;
  createdBy: string;
  workflowOutputs: Record<string, unknown>;
  nextScheduledStep: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type WorkflowAssignmentRequest = {
  case_id: string;
  workflow_id: string;
  workflow_version_id: string;
  instance_type: WorkflowInstanceType;
};

export type WorkflowAssignmentResponse = {
  case: DebtorDetailViewResponse["case"];
  workflow_instance: WorkflowInstanceSchema;
  workflow: WorkflowSchema;
  workflow_version: WorkflowVersionSchema;
};

// ============================================================================
// EMAIL INBOX TYPES
// ============================================================================

export type EmailProviderType = "GMAIL" | "OUTLOOK" | "IMAP";

export type EmailInboxResponse = {
  id: string;
  companyId: string;
  emailAddress: string;
  displayName: string | null;
  providerType: EmailProviderType;
  isSharedInbox: boolean;
  createdBy: string;
  providerAccountId: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
};

export type EmailInboxPaginationResponse = {
  items: EmailInboxResponse[];
  limit: number;
  offset: number;
  count: number;
};

// ============================================================================
// EMAIL MESSAGE TYPES
// ============================================================================

export type EmailMessageResponse = {
  id: string;
  company_id: string;
  email_inbox_id: string;
  case_id: string | null;
  direction: string;
  provider_message_id: string;
  provider_references: string | null;
  provider_thread_id: string | null;
  from_address: string;
  from_name: string | null;
  to_addresses: string[];
  cc_addresses: string[] | null;
  bcc_addresses: string[] | null;
  subject: string;
  snippet: string | null;
  body_text: string | null;
  body_html: string | null;
  sent_at: string | null;
  received_at: string | null;
  is_auto_generated: boolean;
  meta: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

// ============================================================================
// CASE EVENT TYPES
// ============================================================================

export type CaseEventResponse = {
  id: string;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  caseId: string;
  eventType: string;
  createdBy: string;
  data: Record<string, unknown>;
};

// ============================================================================
// CALL TYPES
// ============================================================================

export type CallResponse = {
  id: string;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  caseId: string;
  workflowInstanceId: string | null;
  workflowStepId: string | null;
  source: "WORKFLOW" | "MANUAL" | "API";
  status: "REQUESTED" | "RINGING" | "IN_PROGRESS" | "COMPLETED" | "FAILED" | "NO_ANSWER";
  direction: "OUTBOUND" | "INBOUND";
  purpose: "NORMAL" | "TESTING" | "DEMO";
  agentName: string;
  contextVersion: number;
  debtorPhoneNumber: string;
  operatorInstructions: string | null;
  provider: string;
  conversationId: string | null;
  providerCallSid: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  recordingUrl: string | null;
  summary: string | null;
  structuredSummary: Record<string, unknown> | null;
  generatedEmailSummary: string | null;
  outcome:
    | "SUCCESS"
    | "NO_ANSWER"
    | "BUSY"
    | "FAILED"
    | "VOICEMAIL"
    | "DISPUTED"
    | "PROMISE_MADE"
    | "PAYMENT_ARRANGED"
    | null;
  meta: Record<string, unknown> | null;
};

export type CallEventResponse = {
  id: string;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  callId: string;
  caseId: string;
  eventType: string;
  data: Record<string, unknown>;
};

export type CallTranscriptSegmentResponse = {
  id: string;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  callId: string;
  sequence: number;
  speaker: "AGENT" | "DEBTOR" | "SYSTEM";
  text: string;
  startMs: number | null;
  endMs: number | null;
  confidence: number | null;
  segmentMetadata: Record<string, unknown> | null;
};

export type CallsListResponse = {
  items: CallResponse[];
  count: number;
  limit: number;
  offset: number;
};

export type CallsListParams = {
  case_id?: string;
  status?: string;
  purpose?: string;
  limit?: number;
  offset?: number;
};

export type InitiateCallRequest = {
  caseId: string;
  source: "MANUAL";
  workflowInstanceId?: string | null;
  workflowStepId?: string | null;
  operatorInstructions?: string | null;
  purpose?: "NORMAL" | "TESTING" | "DEMO";
  debtorPhoneNumber?: string; // For test calls
};

export type InitiateCallResponse = {
  callId: string;
};

// ============================================================================
// PAYMENT PLAN TYPES
// ============================================================================

export type PaymentPlanType = "PAY_IN_FULL" | "PAY_IN_ARREARS";

export type PaymentPlanStatus = "DRAFT" | "ACTIVE" | "COMPLETED" | "CANCELLED" | "SUPERSEDED";

export type PaymentFrequency = "WEEKLY" | "BIWEEKLY" | "MONTHLY" | "BIMONTHLY" | "QUARTERLY";

export type PaymentScheduleItemInput = {
  payment_number: number;
  amount: string;
  due_date: string; // ISO date string
  frequency: PaymentFrequency;
};

export type PaymentPlanCreateCommandRequest = {
  case_id: string;
  plan_type: PaymentPlanType;
  total_amount?: string;
  currency?: string;
  due_date?: string; // ISO date string
  schedule?: PaymentScheduleItemInput[];
};

export type PaymentPlanCreateCommandResponse = {
  paymentPlanId: string;
  caseId: string;
  publicToken: string;
  paymentLink: string;
};

export type PaymentPlanResponse = {
  id: string;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  caseId: string;
  createdBy: string;
  planType: PaymentPlanType;
  planData: Record<string, unknown>;
  status: PaymentPlanStatus;
  publicToken: string;
};

export type PaymentPlanPaginationSchema = {
  items: PaymentPlanResponse[];
  count: number;
  limit: number;
  offset: number;
};

export type PaymentPlanPublicViewResponse = {
  debtorName: string;
  currentBalance: string;
  currency: string;
  planType: PaymentPlanType;
  planData: Record<string, unknown>;
  totalAmount: string;
  createdAt: string;
};

export type SendPaymentLinkCommandRequest = {
  paymentPlanId: string;
};

export type GeneratePaymentPlanFromCallRequest = {
  call_id: string;
};

export type GeneratePaymentPlanFromCallResponse = {
  paymentPlanId: string;
  caseId: string;
  planType: PaymentPlanType;
  planData: Record<string, unknown>;
  status: PaymentPlanStatus;
  extractionConfidence: number;
  notes: string | null;
};

export type UpdatePaymentPlanRequest = {
  payment_plan_id: string;
  plan_type?: PaymentPlanType;
  plan_data?: Record<string, unknown>;
};

export type UpdatePaymentPlanResponse = {
  paymentPlan: PaymentPlanResponse;
};

export type ActivatePaymentPlanRequest = {
  payment_plan_id: string;
};

export type ActivatePaymentPlanResponse = {
  paymentPlan: PaymentPlanResponse;
};

// ============================================================================
// ONBOARDING TYPES
// ============================================================================

export type InitialWorkflowTemplate = "STANDARD" | "CUSTOM_FROM_EMAIL_INBOX";

export type OnboardingStatusResponse = {
  companyId: string | null;
  companyName: string | null;
  hasSharedEmailInbox: boolean;
  sharedEmailInbox: EmailInboxResponse | null;
  initialWorkflowTemplate: InitialWorkflowTemplate | null;
  onboardingCompletedAt: string | null;
  /** Derived step: 'company' | 'email-inbox' | 'workflow' | 'completed' */
  currentStep: "company" | "email-inbox" | "workflow" | "completed";
};

export type CompleteOnboardingRequest = {
  company_name?: string;
  initial_workflow_template?: InitialWorkflowTemplate;
};

export type CompleteOnboardingResponse = {
  company_id: string;
  company_name: string;
  initial_workflow_template: InitialWorkflowTemplate | null;
  onboarding_completed_at: string | null;
};

export type RegisterEmailInboxRequest = {
  external_account_id: string;
  is_shared_inbox: boolean;
  email_address: string;
  display_name?: string;
};

export type RegisterEmailInboxResponse = {
  emailInbox: EmailInboxResponse;
};

// ============================================================================
// RESOURCE TYPES
// ============================================================================

export type ResourceType =
  | "EMAIL"
  | "DRAFT_EMAIL_REPLY"
  | "CASE"
  | "RECOMMENDED_ACTION"
  | "TASK"
  | "SMS_MESSAGE"
  | "COMPANY"
  | "USER";

export type Resolution = "ACTIVE" | "ARCHIVED";

// ============================================================================
// FEATURE FLAG TYPES
// ============================================================================

export type FeatureFlagType = string; // e.g., "SHOW_PROFILE_ANALYTICS"

export type AvailableFeatureFlagsResponse = {
  flags: string[]; // List of all available FeatureFlagType enum values as strings
};

export type FeatureFlagResponse = {
  id: string;
  company_id: string;
  feature_flag_type: FeatureFlagType;
  resource_type: ResourceType;
  resource_id: string;
  resolution: Resolution;
  created_by: string | null;
  last_updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type FeatureFlagPaginationResponse = {
  items: FeatureFlagResponse[];
  count: number;
  limit: number;
  offset: number;
};

export type CreateFeatureFlagRequest = {
  company_id: string;
  feature_flag_type: FeatureFlagType;
  resource_type: ResourceType;
  resource_id: string; // company_id for COMPANY type, user_id for USER type
  enabled: boolean; // If true, creates ACTIVE; if false, creates ARCHIVED (or no-op)
};

export type UpdateFeatureFlagRequest = {
  enabled: boolean; // If true, ensures ACTIVE; if false, archives
};

// Specialized API surface for the app
export class BackendApi {
  private client: ApiClient;
  constructor(client?: ApiClient) {
    this.client = client ?? new ApiClient();
  }

  // ============================================================================
  // CALL API METHODS
  // ============================================================================

  async getCalls(params?: CallsListParams): Promise<CallsListResponse> {
    return this.client.request<CallsListResponse>("/api/v1/call/", {
      method: "GET",
      searchParams: {
        case_id: params?.case_id,
        status: params?.status,
        purpose: params?.purpose,
        limit: params?.limit ?? 25,
        offset: params?.offset ?? 0,
      },
    });
  }

  async getCall(callId: string): Promise<CallResponse> {
    return this.client.request<CallResponse>(`/api/v1/call/${callId}`, {
      method: "GET",
    });
  }

  async getCallEvents(callId: string): Promise<CallEventResponse[]> {
    return this.client.request<CallEventResponse[]>(`/api/v1/call/${callId}/events`, {
      method: "GET",
    });
  }

  async getCallTranscript(callId: string): Promise<CallTranscriptSegmentResponse[]> {
    return this.client.request<CallTranscriptSegmentResponse[]>(
      `/api/v1/call/${callId}/transcript`,
      {
        method: "GET",
      },
    );
  }

  async initiateCall(
    caseId: string,
    payload: Omit<InitiateCallRequest, "caseId" | "source">,
  ): Promise<InitiateCallResponse> {
    return this.client.request<InitiateCallResponse>(`/api/v1/call/command/initiate_call`, {
      method: "POST",
      body: JSON.stringify({
        caseId,
        source: "MANUAL",
        ...payload,
      }),
    });
  }

  // ============================================================================
  // DASHBOARD API METHODS
  // ============================================================================

  async getDashboardView(): Promise<DashboardViewResponse> {
    return this.client.request<DashboardViewResponse>("/api/v1/page/dashboard_view", {
      method: "GET",
    });
  }

  // ============================================================================
  // DEBTORS API METHODS
  // ============================================================================

  async getDebtorsListView(params?: {
    query?: string;
    status?: string;
    stage?: string;
    limit?: number;
    offset?: number;
  }): Promise<DebtorsListViewResponse> {
    return this.client.request<DebtorsListViewResponse>("/api/v1/page/debtors_list_view", {
      method: "GET",
      searchParams: params,
    });
  }

  async getDebtorDetailView(caseId: string): Promise<DebtorDetailViewResponse> {
    return this.client.request<DebtorDetailViewResponse>(
      `/api/v1/page/debtor_detail_view/${caseId}`,
      {
        method: "GET",
      },
    );
  }

  // ============================================================================
  // RECOMMENDED ACTIONS API METHODS
  // ============================================================================

  async getRecommendedActionsView(params?: {
    status?: string;
    recommended_action_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<RecommendedActionsViewResponse> {
    return this.client.request<RecommendedActionsViewResponse>(
      "/api/v1/page/recommended_actions_view",
      {
        method: "GET",
        searchParams: {
          status: params?.status,
          recommended_action_type: params?.recommended_action_type,
          limit: params?.limit,
          offset: params?.offset,
        },
      },
    );
  }

  async completeRecommendedAction(payload: {
    recommended_action_id: string;
    actual_body?: string;
    changes_made?: boolean;
    data?: Record<string, unknown>; // Additional data for completion (e.g., workflow_id, workflow_version_id, instance_type)
  }): Promise<{ recommended_action: RecommendedActionsViewResponse["actions"][0] }> {
    return this.client.request("/api/v1/recommended_action/command/complete", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async dismissRecommendedAction(payload: {
    recommended_action_id: string;
  }): Promise<{ recommended_action: RecommendedActionsViewResponse["actions"][0] }> {
    return this.client.request("/api/v1/recommended_action/command/dismiss", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // ============================================================================
  // EMAIL INBOX API METHODS
  // ============================================================================

  async getEmailInboxes(params?: {
    limit?: number;
    offset?: number;
    email_address?: string;
    provider_type?: EmailProviderType;
    is_shared_inbox?: boolean;
    resolution?: string;
  }): Promise<EmailInboxPaginationResponse> {
    return this.client.request<EmailInboxPaginationResponse>("/api/v1/email_inbox/", {
      method: "GET",
      searchParams: {
        limit: params?.limit,
        offset: params?.offset,
        email_address: params?.email_address,
        provider_type: params?.provider_type,
        is_shared_inbox: params?.is_shared_inbox,
        resolution: params?.resolution,
      },
    });
  }

  // ============================================================================
  // EMAIL MESSAGE API METHODS
  // ============================================================================

  async getEmailMessage(emailMessageId: string): Promise<EmailMessageResponse> {
    return this.client.request<EmailMessageResponse>(`/api/v1/email_message/${emailMessageId}`, {
      method: "GET",
    });
  }

  async getEmailMessages(params?: {
    case_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: EmailMessageResponse[]; count: number; limit: number; offset: number }> {
    return this.client.request("/api/v1/email_message/", {
      method: "GET",
      searchParams: params,
    });
  }

  async generateEmailSummaryFromCall(callId: string): Promise<{
    callId: string;
    subject: string;
    body: string;
  }> {
    return this.client.request("/api/v1/email_message/command/generate_summary_from_call", {
      method: "POST",
      body: JSON.stringify({ call_id: callId }),
    });
  }

  async sendEmailSummary(callId: string): Promise<{
    emailMessageId: string;
    caseId: string;
    subject: string;
    sentAt: string;
  }> {
    return this.client.request("/api/v1/email_message/command/send_email_summary", {
      method: "POST",
      body: JSON.stringify({ call_id: callId }),
    });
  }

  // ============================================================================
  // CASE EVENT API METHODS
  // ============================================================================

  async getCaseEvents(params?: {
    case_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: CaseEventResponse[]; count: number; limit: number; offset: number }> {
    return this.client.request("/api/v1/case_event/", {
      method: "GET",
      searchParams: params,
    });
  }

  // ============================================================================
  // CASE COMMAND API METHODS
  // ============================================================================

  async sendCaseEmail(payload: {
    case_id: string;
    subject: string;
    body: string;
    reply_to_email_message_id?: string;
  }): Promise<{ email_message: EmailMessageResponse; case_event: CaseEventResponse }> {
    return this.client.request("/api/v1/case/command/send_email", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async updateCaseOwnership(payload: {
    case_id: string;
    case_owner_id: string | null;
    reason?: string;
  }): Promise<{ case: DebtorDetailViewResponse["case"]; case_event: CaseEventResponse }> {
    return this.client.request("/api/v1/case/command/update_ownership", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async updateCase(
    caseId: string,
    payload: {
      debtor_phone?: string;
      debtor_email?: string;
      debtor_name?: string;
      [key: string]: unknown;
    },
  ): Promise<DebtorDetailViewResponse["case"]> {
    return this.client.request(`/api/v1/case/${caseId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  // ============================================================================
  // DRAFT EMAIL REPLY API METHODS
  // ============================================================================

  async approveDraftEmailReply(payload: {
    draft_email_reply_id: string;
    actual_body?: string;
    changes_made?: boolean;
  }): Promise<{ draft_email_reply: unknown; gmail_message_id: string | null }> {
    return this.client.request("/api/v1/draft_email_reply/command/approve", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // ============================================================================
  // DATA UPLOAD API METHODS
  // ============================================================================

  async getDataUploadView(): Promise<DataUploadViewResponse> {
    return this.client.request<DataUploadViewResponse>("/api/v1/page/data_upload_view", {
      method: "GET",
    });
  }

  async uploadCasesBulk(payload: {
    cases: Array<{
      external_case_reference?: string;
      debtor_name: string;
      debtor_email?: string;
      debtor_phone?: string;
      currency: string;
      original_principal_amount: string;
      current_balance: string;
      due_date?: string;
      stage?: string;
      status?: string;
      case_owner?: string;
      meta?: Record<string, unknown>;
      notes?: string;
    }>;
  }): Promise<{ cases: unknown[]; totalCreated: number }> {
    return this.client.request("/api/v1/case/command/upload_bulk", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // ============================================================================
  // COMPLIANCE & AUDIT API METHODS
  // ============================================================================

  async getComplianceAuditView(params?: {
    query?: string;
    channel?: string;
    outcome?: string;
    agent?: string;
    limit?: number;
    offset?: number;
  }): Promise<ComplianceAuditViewResponse> {
    return this.client.request<ComplianceAuditViewResponse>("/api/v1/page/compliance_audit_view", {
      method: "GET",
      searchParams: params,
    });
  }

  // ============================================================================
  // SETTINGS API METHODS
  // ============================================================================

  async getSettingsView(): Promise<SettingsViewResponse> {
    return this.client.request<SettingsViewResponse>("/api/v1/page/settings_view", {
      method: "GET",
    });
  }

  // ============================================================================
  // USER API METHODS
  // ============================================================================

  async getUserMe(): Promise<UserResponse> {
    return this.client.request<UserResponse>("/api/v1/user/me", {
      method: "GET",
    });
  }

  // ============================================================================
  // WORKFLOW API METHODS
  // ============================================================================

  async listWorkflows(params?: {
    is_default?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<WorkflowPaginationSchema> {
    return this.client.request<WorkflowPaginationSchema>("/api/v1/workflow/", {
      method: "GET",
      searchParams: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  async getWorkflow(workflowId: string): Promise<WorkflowSchema> {
    return this.client.request<WorkflowSchema>(`/api/v1/workflow/${workflowId}`, {
      method: "GET",
    });
  }

  async createWorkflow(payload: WorkflowCreateCommandRequest): Promise<WorkflowSchema> {
    const response = await this.client.request<{ workflow: WorkflowSchema } | WorkflowSchema>(
      "/api/v1/workflow/command/create",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
    if ("workflow" in (response as { workflow?: WorkflowSchema })) {
      return (response as { workflow: WorkflowSchema }).workflow;
    }
    return response as WorkflowSchema;
  }

  async updateWorkflow(payload: WorkflowUpdateCommandRequest): Promise<WorkflowSchema> {
    const response = await this.client.request<{ workflow: WorkflowSchema } | WorkflowSchema>(
      "/api/v1/workflow/command/update",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
    if ("workflow" in (response as { workflow?: WorkflowSchema })) {
      return (response as { workflow: WorkflowSchema }).workflow;
    }
    return response as WorkflowSchema;
  }

  async getWorkflowVersion(workflowId: string, version: number): Promise<WorkflowVersionSchema> {
    return this.client.request<WorkflowVersionSchema>(
      `/api/v1/workflow/${workflowId}/version/${version}`,
      {
        method: "GET",
      },
    );
  }

  async listWorkflowVersions(
    workflowId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<WorkflowVersionPaginationSchema> {
    return this.client.request<WorkflowVersionPaginationSchema>(
      `/api/v1/workflow/${workflowId}/versions`,
      {
        method: "GET",
        searchParams: params as unknown as Record<string, string | number | boolean | undefined>,
      },
    );
  }

  async createWorkflowVersion(
    payload: WorkflowVersionCreateCommandRequest,
  ): Promise<WorkflowVersionSchema> {
    const response = await this.client.request<
      { workflowVersion: WorkflowVersionSchema } | WorkflowVersionSchema
    >("/api/v1/workflow/command/create_version", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    // Handle both wrapped and unwrapped responses
    if ("workflowVersion" in response) {
      return response.workflowVersion;
    }
    return response as WorkflowVersionSchema;
  }

  async updateWorkflowVersion(
    payload: WorkflowVersionUpdateCommandRequest,
  ): Promise<WorkflowVersionSchema> {
    const response = await this.client.request<
      { workflowVersion: WorkflowVersionSchema } | WorkflowVersionSchema
    >("/api/v1/workflow/command/update_version", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    // Handle both wrapped and unwrapped responses
    if ("workflowVersion" in response) {
      return response.workflowVersion;
    }
    return response as WorkflowVersionSchema;
  }

  async publishWorkflowVersion(
    payload: WorkflowVersionPublishCommandRequest,
  ): Promise<WorkflowVersionSchema> {
    const response = await this.client.request<
      { workflowVersion: WorkflowVersionSchema } | WorkflowVersionSchema
    >("/api/v1/workflow/command/publish_version", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    // Handle both wrapped and unwrapped responses
    if ("workflowVersion" in response) {
      return response.workflowVersion;
    }
    return response as WorkflowVersionSchema;
  }

  async archiveWorkflowVersion(
    payload: WorkflowVersionArchiveCommandRequest,
  ): Promise<WorkflowVersionSchema> {
    const response = await this.client.request<
      | { workflow_version: WorkflowVersionSchema }
      | { workflowVersion: WorkflowVersionSchema }
      | WorkflowVersionSchema
    >("/api/v1/workflow/command/archive_version", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    // Handle both wrapped and unwrapped responses
    if ("workflow_version" in response) {
      return response.workflow_version;
    }
    if ("workflowVersion" in response) {
      return response.workflowVersion;
    }
    return response as WorkflowVersionSchema;
  }

  async listPublishedWorkflows(): Promise<WorkflowSchema[]> {
    const response = await this.listWorkflows();
    return response.items.filter((workflow) => workflow.hasPublishedVersion);
  }

  async getWorkflowInstance(workflowInstanceId: string): Promise<WorkflowInstanceSchema> {
    return this.client.request<WorkflowInstanceSchema>(
      `/api/v1/workflow_instance/${workflowInstanceId}`,
      {
        method: "GET",
      },
    );
  }

  // ============================================================================
  // PAYMENT PLAN API METHODS
  // ============================================================================

  async createPaymentPlan(
    payload: PaymentPlanCreateCommandRequest,
  ): Promise<PaymentPlanCreateCommandResponse> {
    return this.client.request<PaymentPlanCreateCommandResponse>(
      "/api/v1/payment_plan/command/create",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  async getPaymentPlan(paymentPlanId: string): Promise<PaymentPlanResponse> {
    return this.client.request<PaymentPlanResponse>(`/api/v1/payment_plan/${paymentPlanId}`, {
      method: "GET",
    });
  }

  async getPaymentPlanByCase(caseId: string): Promise<PaymentPlanResponse | null> {
    const response = await this.client.request<PaymentPlanPaginationSchema>(
      "/api/v1/payment_plan/",
      {
        method: "GET",
        searchParams: {
          case_id: caseId,
        },
      },
    );
    // Return the first active payment plan, or null if none found
    return response.items.length > 0 ? response.items[0] : null;
  }

  async getPublicPaymentPlan(
    caseId: string,
    paymentPlanId: string,
    token: string,
  ): Promise<PaymentPlanPublicViewResponse> {
    return this.client.request<PaymentPlanPublicViewResponse>(
      "/api/v1/page/public_payment_plan_view",
      {
        method: "GET",
        searchParams: {
          case_id: caseId,
          payment_plan_id: paymentPlanId,
          token: token,
        },
      },
    );
  }

  async sendPaymentLink(payload: SendPaymentLinkCommandRequest): Promise<void> {
    return this.client.request<void>("/api/v1/payment_plan/command/send_link", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async generatePaymentPlanFromCall(
    payload: GeneratePaymentPlanFromCallRequest,
  ): Promise<GeneratePaymentPlanFromCallResponse> {
    return this.client.request<GeneratePaymentPlanFromCallResponse>(
      "/api/v1/payment_plan/command/generate_from_call",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  async updatePaymentPlan(payload: UpdatePaymentPlanRequest): Promise<UpdatePaymentPlanResponse> {
    return this.client.request<UpdatePaymentPlanResponse>("/api/v1/payment_plan/command/update", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async activatePaymentPlan(
    payload: ActivatePaymentPlanRequest,
  ): Promise<ActivatePaymentPlanResponse> {
    return this.client.request<ActivatePaymentPlanResponse>(
      "/api/v1/payment_plan/command/activate",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  // ============================================================================
  // ONBOARDING API METHODS
  // ============================================================================

  async getOnboardingStatus(): Promise<OnboardingStatusResponse> {
    return this.client.request<OnboardingStatusResponse>("/api/v1/company/onboarding_status", {
      method: "GET",
    });
  }

  async completeOnboarding(
    payload: CompleteOnboardingRequest,
  ): Promise<CompleteOnboardingResponse> {
    return this.client.request<CompleteOnboardingResponse>(
      "/api/v1/company/command/complete_onboarding",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  async registerEmailInbox(
    payload: RegisterEmailInboxRequest,
  ): Promise<RegisterEmailInboxResponse> {
    return this.client.request<RegisterEmailInboxResponse>("/api/v1/email_inbox/command/register", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // ============================================================================
  // FEATURE FLAG API METHODS
  // ============================================================================

  async getAllAvailableFeatureFlags(): Promise<AvailableFeatureFlagsResponse> {
    return this.client.request<AvailableFeatureFlagsResponse>(
      "/api/v1/feature_flag/all_available",
      {
        method: "GET",
      },
    );
  }

  async getCompanyFeatureFlags(companyId: string): Promise<FeatureFlagPaginationResponse> {
    return this.client.request<FeatureFlagPaginationResponse>("/api/v1/feature_flag/", {
      method: "GET",
      searchParams: {
        company_id: companyId,
        resource_type: "COMPANY",
        resolution: "ACTIVE",
        limit: 100,
        offset: 0,
      },
    });
  }

  async createFeatureFlag(payload: CreateFeatureFlagRequest): Promise<FeatureFlagResponse> {
    return this.client.request<FeatureFlagResponse>("/api/v1/feature_flag/", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async updateFeatureFlag(
    flagId: string,
    payload: UpdateFeatureFlagRequest,
  ): Promise<FeatureFlagResponse> {
    return this.client.request<FeatureFlagResponse>(`/api/v1/feature_flag/${flagId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  // ============================================================================
  // ROLLOUT CONTROL API METHODS (staff only, via the /api/v1/internal proxies)
  // ============================================================================

  async getPlatformControl(): Promise<PlatformControl> {
    return this.client.request<PlatformControl>("/api/v1/internal/platform_control", {
      method: "GET",
    });
  }

  async pausePlatformAutomation(payload: PauseReasonRequest): Promise<PlatformControl> {
    return this.client.request<PlatformControl>(
      "/api/v1/internal/platform_control/automation_pause",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  async resumePlatformAutomation(): Promise<PlatformControl> {
    return this.client.request<PlatformControl>(
      "/api/v1/internal/platform_control/automation_resume",
      { method: "POST" },
    );
  }

  async pauseCompanyAutomation(
    companyId: string,
    payload: PauseReasonRequest,
  ): Promise<InternalCompanySchema> {
    return this.client.request<InternalCompanySchema>(
      `/api/v1/internal/company/${encodeURIComponent(companyId)}/automation_pause`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }

  async resumeCompanyAutomation(companyId: string): Promise<InternalCompanySchema> {
    return this.client.request<InternalCompanySchema>(
      `/api/v1/internal/company/${encodeURIComponent(companyId)}/automation_resume`,
      { method: "POST" },
    );
  }

  async setCompanyOperatingMode(
    companyId: string,
    payload: OperatingModeRequest,
  ): Promise<InternalCompanySchema> {
    return this.client.request<InternalCompanySchema>(
      `/api/v1/internal/company/${encodeURIComponent(companyId)}/operating_mode`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }
}
